import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Briefcase, Settings } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { usePermission } from '@/hooks/usePermission';
import { tasksService } from '@/services/tasks.service';
import { CreateTaskModal } from '@/components/tasks/CreateTaskModal';
import { AdditionalWorkModal } from '@/components/tasks/AdditionalWorkModal';
import { AllEmployeesTab } from './tabs/AllEmployeesTab';
import { AssignedByMeTab } from './tabs/AssignedByMeTab';
import { MyTasksTab } from './tabs/MyTasksTab';
import { ByTasksTab } from './tabs/ByTasksTab';
import { ReviewTab } from './tabs/ReviewTab';

type TopTab = 'all-employees' | 'assigned-by-me' | 'my-tasks' | 'by-tasks' | 'review';
type Mode = 'all' | 'stopped' | 'report';

export default function TaskManagementPage() {
  const { has } = usePermission();
  const canViewTeam = has(PERMISSIONS.TASK_VIEW_TEAM) || has(PERMISSIONS.TASK_VIEW_ALL);
  const canViewAll = has(PERMISSIONS.TASK_VIEW_ALL) || has(PERMISSIONS.TASK_VIEW_TEAM);
  const canCreate = has(PERMISSIONS.TASK_CREATE);
  // Reviewing is the task creator's job, so someone who cannot create tasks can never
  // have a queue. Both are required because task.review has drifted onto roles that
  // hold no task.create (USER, on the live DB), which would show them an empty tab.
  const canReview = has(PERMISSIONS.TASK_REVIEW) && has(PERMISSIONS.TASK_CREATE);
  const canLogWork = has(PERMISSIONS.ADDITIONAL_TASK_CREATE);

  // Polled like the notification bell: the tab is an attention signal, so a reviewer
  // sitting on the page should see new submissions arrive without a manual refresh.
  const reviewQ = useQuery({
    queryKey: ['tasks', 'review-count'],
    queryFn: () => tasksService.reviewCount(),
    enabled: canReview,
    refetchInterval: 60_000,
  });
  const pendingReviews = reviewQ.data ?? 0;

  const tabs = useMemo(() => {
    const list: { key: TopTab; label: ReactNode }[] = [];
    if (canViewTeam) list.push({ key: 'all-employees', label: 'All Employees' });
    if (canCreate) list.push({ key: 'assigned-by-me', label: 'Assigned by Me' });
    list.push({ key: 'my-tasks', label: 'My Tasks' });
    if (canViewAll) list.push({ key: 'by-tasks', label: 'By Tasks' });
    if (canReview) {
      list.push({
        key: 'review',
        label: (
          <span className="flex items-center gap-1.5">
            Review
            {pendingReviews > 0 && (
              <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                {pendingReviews > 9 ? '9+' : pendingReviews}
              </span>
            )}
          </span>
        ),
      });
    }
    return list;
  }, [canViewTeam, canViewAll, canCreate, canReview, pendingReviews]);

  const [tab, setTab] = useState<TopTab>(() => (canViewTeam ? 'all-employees' : 'my-tasks'));
  const [mode, setMode] = useState<Mode>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);

  const stoppedQ = useQuery({
    queryKey: ['tasks', 'stopped-count'],
    queryFn: () => tasksService.stoppedCount(),
    enabled: canViewAll,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">Task Management</h1>
          <p className="text-sm text-muted-foreground">Track, manage and collaborate on your assigned tasks</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canViewAll && (
            <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
              <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tasks</SelectItem>
                <SelectItem value="stopped">Stopped Tasks{stoppedQ.data ? ` (${stoppedQ.data})` : ''}</SelectItem>
                <SelectItem value="report">Report (Snapshot)</SelectItem>
              </SelectContent>
            </Select>
          )}
          {canLogWork && (
            <Button variant="outline" onClick={() => setWorkOpen(true)}>
              <Briefcase className="h-4 w-4" /> Additional Task
            </Button>
          )}
          <PermissionGate permission={PERMISSIONS.TASK_CONFIG}>
            <Button asChild variant="outline" size="icon" title="Task Configuration">
              <Link to="/tasks/config"><Settings className="h-4 w-4" /></Link>
            </Button>
          </PermissionGate>
          {canCreate && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Create Task
            </Button>
          )}
        </div>
      </div>

      {mode === 'report' && (
        <div className="rounded-xl border border-dashed border-border/60 bg-card p-10 text-center">
          <p className="text-sm font-medium text-foreground">Report (Snapshot) view</p>
          <p className="mt-1 text-sm text-muted-foreground">
            The read-only daily snapshot report arrives in the next phase.
          </p>
        </div>
      )}

      {mode === 'stopped' && (
        <ByTasksTab lifecycleState="stopped" />
      )}

      {mode === 'all' && (
        <>
          {tabs.length > 1 && (
            <Tabs active={tab} onChange={(k) => setTab(k as TopTab)} tabs={tabs} />
          )}
          {tab === 'all-employees' && canViewTeam && <AllEmployeesTab />}
          {tab === 'assigned-by-me' && canCreate && <AssignedByMeTab />}
          {tab === 'my-tasks' && <MyTasksTab />}
          {tab === 'by-tasks' && canViewAll && <ByTasksTab />}
          {tab === 'review' && canReview && <ReviewTab />}
        </>
      )}

      <CreateTaskModal open={createOpen} onOpenChange={setCreateOpen} />
      <AdditionalWorkModal open={workOpen} onOpenChange={setWorkOpen} />
    </div>
  );
}
